import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import PageHeader from '../includes/PageHeader';
import showMessage from '../../../helpers/showMessage';
import { List, ListItem, Box, Tabs, Tab, Typography, Button, TextField } from '@mui/material';
import ProcessGraph from './ProcessGraph';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, differenceInCalendarDays, startOfWeek, endOfWeek } from 'date-fns';
import { getDataList } from '../../../helpers/getDataList';

const MaxDiffDay = 6;

export function EpgProcess() {
    const [startDate, setStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [endDate, setEndDate] = useState(endOfWeek(new Date(), { weekStartsOn: 1 }));
    const [process, setProcess] = useState({});
    const [tabIndex, setTabIndex] = useState(0);
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const { enqueueSnackbar } = useSnackbar();
    
    useEffect(() => {
        if (startDate && endDate) {
            const tempErrors = validate();
            setShowSaveButton(Object.keys(tempErrors).length === 0);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (startDate && endDate) {
            handleSubmit();
        }
    }, []);

    const validate = () => {
        let tempErrors = {};
        const requiredMessage = 'Please fill out this field';

        if (!startDate) tempErrors.startDate = requiredMessage;
        if (!endDate) tempErrors.endDate = requiredMessage;

        const diff = differenceInCalendarDays(new Date(endDate), new Date(startDate));

        if (diff !== MaxDiffDay) {
            tempErrors.startDate = tempErrors.endDate = 'Date range must be exactly 6 days';
            showMessage('Date range must be exactly 6 days', enqueueSnackbar, 'error');
        }

        setErrors(tempErrors);
        return tempErrors;
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        const formattedStartDate = format(new Date(startDate), 'yyyy/MM/dd');
        const formattedEndDate = format(new Date(endDate), 'yyyy/MM/dd');
        const query = new URLSearchParams({
            startDate: formattedStartDate,
            endDate: formattedEndDate
        }).toString();

        getDataList(`channel/epg/process?${query}`)
            .then((response) => {
                setProcess(response.data?.data.process || {}); 
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                const message = error.response?.data?.message
                    ? error.response.data.message
                    : error.message;
                showMessage(message, enqueueSnackbar, 'error');
            });
    };

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    return (
        <>
            <div>
                <PageHeader
                    pageName="Хөтөлбөр оруулалтын явц шүүх"
                    showAddNew={false}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                    <div style={{ marginLeft: '10px' }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DesktopDatePicker
                                label="Эхлэх өдөр"
                                inputFormat="yyyy/MM/dd"
                                mask="____/__/__"
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                                renderInput={(params) => <TextField {...params} error={!!errors.startDate} helperText={errors.startDate} />}
                            />
                            <DesktopDatePicker
                                label="Дуусах өдөр"
                                inputFormat="yyyy/MM/dd"
                                mask="____/__/__"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                renderInput={(params) => <TextField {...params} error={!!errors.endDate} helperText={errors.endDate} />}
                            />
                        </LocalizationProvider>
                    </div>

                    {showSaveButton && 
                        <Button
                            color="success"
                            style={{ marginLeft: '10px', width: '150px', height: '50px' }}
                            variant="outlined"
                            onClick={handleSubmit}
                        >
                            <Typography style={{ marginLeft: '10px' }}>
                                Хайх
                            </Typography>
                        </Button>
                    }
                </div>

                {!isLoading && (
                    process && Object.keys(process).length > 0  ? 
                        <div>
                            <ProcessGraph graphData={process} /> {/* Pass process as a prop */}
                            <Box sx={{ width: '100%', marginTop: 2 }}>
                                <Tabs value={tabIndex} onChange={handleTabChange} aria-label="tabs">
                                    <Tab label="Хөтөлбөр нийтлэгдсэн сувгууд" />
                                    <Tab label="Хөтөлбөр оруулсан сувгууд" />
                                    <Tab label="Хөтөлбөр ороогүй сувгууд" />
                                </Tabs>
                                <Box sx={{ padding: 2 }}>
                                    {tabIndex === 0 && (
                                        <>
                                            <Typography variant="h6">Хөтөлбөр нийтлэгдсэн сувгийн тоо: {process.executedEpgChannelsCount || 0}</Typography>
                                            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                                                <List>
                                                    {process.executedEpgChannels && process.executedEpgChannels.map((channel, index) => (
                                                        <ListItem key={index}>
                                                            {channel.name}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        </>
                                    )}
                                    {tabIndex === 1 && (
                                        <>
                                            <Typography variant="h6">Хөтөлбөр оруулсан сувгийн тоо: {process.foundEpgChannelsCount || 0}</Typography>
                                            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                                                <List>
                                                    {process.foundEpgChannels && process.foundEpgChannels.map((channel, index) => (
                                                        <ListItem key={index}>
                                                            {channel.name}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        </>
                                    )}
                                    {tabIndex === 2 && (
                                        <>
                                            <Typography variant="h6">Хөтөлбөр ороогүй сувгийн тоо: {process.notFoundEpgChannelsCount || 0}</Typography>
                                            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                                                <List>
                                                    {process.notFoundEpgChannels && process.notFoundEpgChannels.map((channel, index) => (
                                                        <ListItem key={index}>
                                                            {channel.name}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </div> : 
                        <Box sx={{ textAlign: 'center', marginTop: 5 }}>
                            <Typography variant="h6" color="error">
                                Энэ 7 хоногийн хөтөлбөр олдсонгүй.
                            </Typography>
                        </Box>
                )}
            </div>
        </>
    );
}
